import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import { Download, Pencil, Check, Trash2, KeyRound, X, Sparkles } from 'lucide-react';
import {
  updateArtifact, reviseArtifact, downloadArtifact, deleteArtifact,
  listArtifactImages, rejectArtifactImage, getTrainingCapabilities,
  downloadArtifactImage, updateArtifactImage, anchorScriptImage, captionSavedImage,
} from './api';
import ArtifactEditor from './ArtifactEditor';
import ImageComposer from './ImageComposer';
import ImageBubbleMenu from './ImageBubbleMenu';
import QuizEditor from './QuizEditor';
import AvScriptEditor from './AvScriptEditor';

// Formatting for the read-only view. Shared by the legacy Markdown branch and
// the HTML branch so both render identically.
const MD = 'text-sm text-[#c7d0e0] leading-relaxed ' +
  '[&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-3 [&_h1]:mb-1 ' +
  '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:font-semibold [&_h3]:mt-2 ' +
  '[&_p]:my-2 [&_strong]:font-semibold [&_strong]:text-[#e6edf6] [&_em]:italic ' +
  '[&_u]:underline [&_s]:line-through ' +
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:my-0.5 ' +
  '[&_table]:w-full [&_table]:my-3 [&_table]:border-collapse ' +
  '[&_th]:border [&_th]:border-[#2b3a4d] [&_th]:bg-[#12202f] [&_th]:px-2 [&_th]:py-1 [&_th]:text-left ' +
  '[&_td]:border [&_td]:border-[#2b3a4d] [&_td]:px-2 [&_td]:py-1 [&_a]:text-[#5ea9ff] [&_a]:underline ' +
  '[&_pre]:bg-[#0b0e13] [&_pre]:border [&_pre]:border-[#2b3a4d] [&_pre]:rounded [&_pre]:p-2 [&_pre]:my-2 ' +
  '[&_pre]:overflow-auto [&_pre]:text-[11px] [&_code]:font-mono [&_code]:text-[#c7d0e0] ' +
  '[&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-2 ' +
  '[&_blockquote]:border-l-4 [&_blockquote]:border-[#2b3a4d] [&_blockquote]:pl-3 [&_blockquote]:italic ' +
  '[&_figure]:my-3 [&_figure]:text-center ' +
  '[&_figcaption]:text-[11px] [&_figcaption]:italic [&_figcaption]:mt-1 [&_figcaption]:text-[#8a95a8] ' +
  '[&_figcaption\\[data-pending=true\\]]:text-[#f7a23b]';


// The caption lives on the image record, not in the content HTML, so both
// surfaces have to be told about it or the editor and the viewer show a bare
// image while the .docx shows a numbered caption.
//
// Figure numbers here match the renderer exactly: covers are inserted at the top
// of the document and numbered first, and a pending image never reaches the
// .docx so it never consumes a number.
function captionsById(images) {
  const map = new Map();
  (images || []).forEach((i) => map.set(String(i.id), i));
  return map;
}

// For the editor: refresh each node's display-only data-caption from the record.
function hydrateCaptions(html, byId) {
  if (!html || byId.size === 0) return html || '';
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  doc.querySelectorAll('img[data-training-image-id]').forEach((el) => {
    const rec = byId.get(String(el.getAttribute('data-training-image-id')));
    if (!rec) return;
    if (rec.caption) el.setAttribute('data-caption', rec.caption);
    else el.removeAttribute('data-caption');
    el.setAttribute('data-status', rec.status || 'draft');
  });
  return doc.body.firstChild ? doc.body.firstChild.innerHTML : html;
}

// For the read-only viewer: wrap each image in a real figure with a numbered
// caption, so what you read matches what you download.
function toViewerHtml(html, byId, coverCount) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  let figure = coverCount;
  doc.querySelectorAll('img[data-training-image-id]').forEach((el) => {
    const rec = byId.get(String(el.getAttribute('data-training-image-id')));
    const approved = rec?.status === 'approved';
    if (approved) figure += 1;
    const text = rec?.caption || '';
    const label = approved
      ? `Figure ${figure}.${text ? ` ${text}` : ''}`
      : `${text}${text ? ' ' : ''}(pending approval, not in the .docx)`;

    const fig = doc.createElement('figure');
    const cap = doc.createElement('figcaption');
    cap.textContent = label;
    cap.setAttribute('data-pending', approved ? 'false' : 'true');
    el.parentNode.insertBefore(fig, el);
    fig.appendChild(el);
    fig.appendChild(cap);
  });
  return doc.body.firstChild ? doc.body.firstChild.innerHTML : html;
}

function QuizView({ content }) {
  const [showKey, setShowKey] = useState(false);
  let quiz = null;
  try { quiz = typeof content === 'string' ? JSON.parse(content) : content; } catch { quiz = null; }
  const questions = quiz?.questions || [];
  // Never surface raw JSON to the user - show a friendly prompt instead.
  if (!questions.length) return (
    <div className="text-xs text-[#8a95a8]">
      No quiz questions yet. Use “Steer with JPRO” below (e.g. “write 10 knowledge-check questions”) or regenerate.
    </div>
  );
  return (
    <div className="text-sm text-[#c7d0e0]">
      <div className="flex justify-end mb-2">
        <button onClick={() => setShowKey((v) => !v)} className="text-[11px] text-[#5ea9ff]">
          {showKey ? 'Hide answer key' : 'Show answer key / marking scheme'}
        </button>
      </div>
      <ol className="list-decimal pl-5 space-y-4">
        {questions.map((q, i) => (
          <li key={i}>
            <div className="font-medium text-[#e6edf6]">{q.prompt}</div>
            <ul className="mt-1 space-y-0.5">
              {(q.choices || []).map((ch, ci) => (
                <li key={ci} className={showKey && ci === q.answer_index ? 'text-green-400' : 'text-[#c7d0e0]'}>
                  {String.fromCharCode(65 + ci)}. {ch}{showKey && ci === q.answer_index ? '  ✓' : ''}
                </li>
              ))}
            </ul>
            {showKey && q.rationale && <div className="text-[11px] text-[#8a95a8] mt-1">{q.rationale}</div>}
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function ArtifactDetail({ artifact, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [editor, setEditor] = useState(null);
  // null = closed. {} = new inline image. {imageId} = refine. {placement:'cover'} = cover.
  const [composer, setComposer] = useState(null);
  // Which image's caption is being edited, plus the draft values.
  const [captionEdit, setCaptionEdit] = useState(null);
  // Suggests a caption and alt text from the picture. A suggestion only: it fills the
  // fields and the author still presses Save, so nothing is written on their behalf.
  const suggestCaption = useMutation({
    mutationFn: (id) => captionSavedImage(id),
    onSuccess: (res) => {
      // A non-2xx lands in onError, so anything here is a real answer.
      if (!res?.caption && !res?.alt_text) {
        toast.error('JPRO could not describe that image. Type a caption instead.');
        return;
      }
      setCaptionEdit((c) => (c ? {
        ...c,
        caption: res.caption || c.caption,
        alt_text: res.alt_text || c.alt_text,
      } : c));
      toast.success('Suggested. Edit it, then save.');
    },
    onError: (e) => toast.error(e?.message || 'Could not suggest a caption'),
  });

  const { data: caps } = useQuery({ queryKey: ['training-me'], queryFn: getTrainingCapabilities });
  const canApproveContent = Boolean(caps?.can_approve_content);

  const { data: imageData } = useQuery({
    queryKey: ['artifact-images', artifact.id],
    queryFn: () => listArtifactImages(artifact.id),
  });
  const images = imageData?.images || [];
  const cover = images.find((i) => i.placement === 'cover');
  const byId = captionsById(images);
  const coverCount = images.filter((i) => i.placement === 'cover' && i.status === 'approved').length;
  const pending = images.filter((i) => i.status === 'draft' && i.placement !== 'cover');

  const qc = useQueryClient();
  // Image mutations change the image list, not just the concept, so both caches
  // have to be invalidated or the pending banner and cover row go stale.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['artifact-images', artifact.id] });
    onChanged?.();
  };

  const saveMut = useMutation({
    mutationFn: (html) => updateArtifact(artifact.id, { generated_content: html }),
    onSuccess: () => { toast.success('Saved'); setEditing(false); refresh(); },
    onError: (e) => toast.error(e?.message || 'Save failed'),
  });
  const reviseMut = useMutation({
    mutationFn: () => reviseArtifact(artifact.id, instruction),
    onSuccess: () => { toast.success('JPRO revised the artifact'); setInstruction(''); refresh(); },
    onError: (e) => toast.error(e?.message || 'Revision failed'),
  });
  const approveMut = useMutation({
    mutationFn: () => updateArtifact(artifact.id, { status: 'approved' }),
    onSuccess: () => { toast.success('Approved'); refresh(); },
    onError: (e) => toast.error(e?.message || 'Approve failed'),
  });
  const downloadMut = useMutation({
    mutationFn: (a) => downloadArtifact(a.id, a.title, a.variant),
    onError: (e) => toast.error(e?.message || 'Download failed'),
  });
  const deleteMut = useMutation({
    mutationFn: () => deleteArtifact(artifact.id),
    onSuccess: () => { toast.success('Artifact deleted'); refresh(); },
    onError: (e) => toast.error(e?.message || 'Delete failed'),
  });
  const downloadImage = useMutation({
    mutationFn: (img) => downloadArtifactImage(
      img.url,
      `${(artifact.title || 'artifact').replace(/[^A-Za-z0-9 _-]/g, '').trim() || 'artifact'} - ${img.placement} ${img.id}.png`,
    ),
    onError: (e) => toast.error(e?.message || 'Download failed'),
  });
  const saveCaption = useMutation({
    mutationFn: ({ id, caption, alt_text }) => updateArtifactImage(id, { caption, alt_text }),
    onSuccess: (updated, vars) => {
      toast.success('Caption updated');
      // Push the new caption into the live node so the editor shows it straight
      // away. The node attribute is display only; the record is the authority.
      if (editor) {
        const { state, view } = editor;
        state.doc.descendants((node, pos) => {
          if (node.type.name !== 'image') return;
          if (String(node.attrs['data-training-image-id']) !== String(vars.id)) return;
          view.dispatch(state.tr.setNodeMarkup(pos, undefined, {
            ...node.attrs, 'data-caption': vars.caption || null, alt: vars.alt_text || '',
          }));
        });
      }
      setCaptionEdit(null);
      refresh();
    },
    onError: (e) => toast.error(e?.message || 'Could not update the caption'),
  });
  const removeCover = useMutation({
    mutationFn: (id) => rejectArtifactImage(id),
    onSuccess: () => { toast.success('Cover removed'); refresh(); },
    onError: (e) => toast.error(e?.message || 'Not permitted'),
  });

  const isQuiz = artifact.format === 'json';
  // An A/V script is structured, chapter-addressable, and saves per chapter rather than
  // as one blob, so it owns its whole surface instead of sitting inside the prose editor.
  const isAvScript = artifact.format === 'av_script';
  // An A/V script has no edit MODE: the chapter editor is always live and saves per
  // chapter. The pencil rendered anyway and did nothing when clicked.
  const showEditToggle = !isAvScript;

  // Remove is the only way out of a generation that never finishes, so both
  // panels below offer it. Deleting mid-run is safe: the job's write to a
  // deleted row updates nothing.
  const removeButton = canApproveContent && (
    <button onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}
      className="text-xs px-3 py-1.5 rounded-lg bg-[#1f2a38] border border-[#2b3a4d] text-[#ffb4b4] disabled:opacity-40">
      Remove
    </button>
  );

  // Generation runs in a background job. Until it lands there is nothing to
  // edit, and the server refuses edits/steering/download anyway - the job is
  // about to overwrite the row. Show what is happening instead of an empty
  // editor. The list polls, so this swaps itself out when the content arrives.
  if (artifact.status === 'generating' && !artifact.generation_stalled) return (
    <div className="text-sm text-[#8a95a8]">
      <div className="text-sm font-semibold text-[#e6edf6] mb-1">{artifact.title}</div>
      <div className="flex items-center gap-2 text-[#9ec5ff] mb-2">
        <span className="w-2 h-2 rounded-full bg-[#5ea9ff] animate-pulse" />
        JPRO is writing this {artifact.kind}.
      </div>
      <p className="text-xs max-w-md mb-3">
        A full script can take a few minutes. This fills in on its own, and you can leave
        the page while it works.
      </p>
      {removeButton}
    </div>
  );

  if (artifact.status === 'failed' || artifact.generation_stalled) return (
    <div className="text-sm">
      <div className="text-sm font-semibold text-[#e6edf6] mb-1">{artifact.title}</div>
      <div className="text-xs text-[#ffb4b4] mb-3 max-w-md">
        {artifact.generation_error ||
          (artifact.generation_stalled
            ? 'JPRO never came back with this one, so it will not finish on its own.'
            : 'JPRO could not finish writing this artifact.')}
      </div>
      <p className="text-xs text-[#8a95a8] mb-3 max-w-md">
        Nothing was written. Remove this one and add it again to retry - the lead-up
        questions let you narrow what you asked for.
      </p>
      {removeButton}
    </div>
  );

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold">{artifact.title}</div>
          <div className="text-[11px] text-[#8a95a8]">{artifact.kind} · {artifact.status}</div>
        </div>
        <div className="flex items-center gap-1">
          {isQuiz ? (
            <>
              <button title="Download question sheet (no answers)"
                onClick={() => downloadMut.mutate({ ...artifact, variant: 'questions' })}
                disabled={downloadMut.isPending}
                className="p-1.5 rounded hover:bg-[#12202f] text-[#5ea9ff] disabled:opacity-40"><Download size={15} /></button>
              <button title="Download answer key / marking scheme"
                onClick={() => downloadMut.mutate({ ...artifact, variant: 'answer_key' })}
                disabled={downloadMut.isPending}
                className="p-1.5 rounded hover:bg-[#12202f] text-[#5ea9ff] disabled:opacity-40"><KeyRound size={15} /></button>
            </>
          ) : (
            <button title="Download .docx" onClick={() => downloadMut.mutate(artifact)}
              disabled={downloadMut.isPending}
              className="p-1.5 rounded hover:bg-[#12202f] text-[#5ea9ff] disabled:opacity-40"><Download size={15} /></button>
          )}
          {showEditToggle && !editing && (
            <button title="Edit" onClick={() => setEditing(true)}
              className="p-1.5 rounded hover:bg-[#12202f] text-[#5ea9ff]"><Pencil size={15} /></button>
          )}
          {artifact.status === 'draft' && (
            <button title="Approve" onClick={() => approveMut.mutate()}
              className="p-1.5 rounded hover:bg-[#12202f] text-green-400"><Check size={16} /></button>
          )}
          <button title="Delete artifact"
            onClick={() => {
              if (window.confirm(`Delete "${artifact.title}"? This cannot be undone.`)) deleteMut.mutate();
            }}
            disabled={deleteMut.isPending}
            className="p-1.5 rounded hover:bg-[#12202f] text-[#f7a23b] disabled:opacity-40"><Trash2 size={15} /></button>
        </div>
      </div>

      {/* Cover image: a document-level property, so it has no cursor position and
          cannot live as a node in the body. */}
      {!isQuiz && (
        <div className="mb-2 flex items-center gap-2 text-[11px] text-[#8a95a8]">
          <span className="uppercase tracking-wide text-[10px] text-[#7d8aa0]">Cover image</span>
          {cover ? (
            <>
              {cover.url && <img src={cover.url} alt="" className="h-8 w-8 object-cover rounded" />}
              <span className={cover.status === 'draft' ? 'text-[#f7a23b]' : 'text-blue-400'}>
                {cover.status === 'draft' ? 'Pending approval' : 'Approved'}
              </span>
              <button
                onClick={() => {
                  if (window.confirm('Remove the cover image?')) removeCover.mutate(cover.id);
                }}
                className="text-[#f7a23b]"
              >
                remove
              </button>
            </>
          ) : (
            <button onClick={() => setComposer({ placement: 'cover' })} className="text-[#5ea9ff]">
              add a cover
            </button>
          )}
        </div>
      )}

      {/* Pending inline images: a sweep list so an approver can clear several at
          once rather than clicking each one in the body. */}
      {pending.length > 0 && (
        <div className="mb-2 rounded-lg border border-[#f7a23b]/40 bg-[#f7a23b]/5 px-2 py-1.5">
          <span className="text-[11px] text-[#f7a23b]">
            {pending.length} inline image{pending.length > 1 ? 's' : ''} pending approval.
            {' '}They will not appear in the downloaded .docx until approved.
          </span>
        </div>
      )}

      {/* Every generated image for this artifact, downloadable on its own.
          "Standalone" images are deliberately never embedded in the .docx, for
          when you want the asset without it sitting in the text. */}
      {!isQuiz && images.length > 0 && (
        <div className="mb-3 rounded-lg border border-[#1d2634] bg-[#0b0e13] p-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] uppercase tracking-wide text-[#7d8aa0]">
              Generated images
            </span>
            <button onClick={() => setComposer({ placement: 'standalone' })}
              className="text-[11px] text-[#5ea9ff]">
              + standalone image
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {images.map((img) => (
              <div key={img.id}
                className="rounded border border-[#2b3a4d] px-1.5 py-1">
                <div className="flex items-center gap-1.5">
                  {img.url && (
                    <img src={img.url} alt={img.alt_text || ''}
                      className="h-8 w-8 object-cover rounded" />
                  )}
                  <div className="flex flex-col min-w-[92px]">
                    <span className="text-[10px] text-[#c7d0e0]">
                      {img.placement}
                      {img.placement === 'standalone' && (
                        <span className="text-[#5f6b7e]"> (not in the .docx)</span>
                      )}
                    </span>
                    <span className={`text-[10px] ${
                      img.status === 'draft' ? 'text-[#f7a23b]' : 'text-blue-400'}`}>
                      {img.status === 'draft' ? 'pending' : 'approved'}
                    </span>
                    <span className="text-[10px] text-[#5f6b7e] max-w-[140px] truncate"
                      title={img.caption || 'no caption'}>
                      {img.caption || 'no caption'}
                    </span>
                  </div>
                  {canApproveContent && (
                    <button
                      title="Edit caption and alt text"
                      onClick={() => setCaptionEdit({
                        id: img.id, caption: img.caption || '', alt_text: img.alt_text || '',
                      })}
                      className="p-1 rounded hover:bg-[#12202f] text-[#5ea9ff]"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                  <button
                    title="Download this image"
                    onClick={() => downloadImage.mutate(img)}
                    disabled={downloadImage.isPending || !img.url}
                    className="p-1 rounded hover:bg-[#12202f] text-[#5ea9ff] disabled:opacity-40"
                  >
                    <Download size={13} />
                  </button>
                </div>

                {captionEdit?.id === img.id && (
                  <div className="mt-1.5 space-y-1 border-t border-[#1d2634] pt-1.5">
                    <input
                      value={captionEdit.caption}
                      onChange={(e) => setCaptionEdit((c) => ({ ...c, caption: e.target.value }))}
                      placeholder="Caption (shown under the figure)"
                      className="w-full text-[11px] bg-transparent border border-[#2b3a4d] rounded px-2 py-1 text-[#c7d0e0]"
                    />
                    <input
                      value={captionEdit.alt_text}
                      onChange={(e) => setCaptionEdit((c) => ({ ...c, alt_text: e.target.value }))}
                      placeholder="Alt text (for screen readers)"
                      className="w-full text-[11px] bg-transparent border border-[#2b3a4d] rounded px-2 py-1 text-[#c7d0e0]"
                    />
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => saveCaption.mutate(captionEdit)}
                        disabled={saveCaption.isPending}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-[#1f6feb] text-white disabled:opacity-40"
                      >
                        <Check size={11} /> {saveCaption.isPending ? 'Saving' : 'Save'}
                      </button>
                      <button
                        onClick={() => suggestCaption.mutate(img.id)}
                        disabled={suggestCaption.isPending}
                        title="Have JPRO look at the image and suggest a caption and alt text"
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[#2b3a4d] text-[#5ea9ff] disabled:opacity-40"
                      >
                        <Sparkles size={11} />
                        {suggestCaption.isPending ? 'Reading…' : 'Suggest with JPRO'}
                      </button>
                      <button onClick={() => setCaptionEdit(null)}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 text-[#8a95a8]">
                        <X size={11} /> Cancel
                      </button>
                      <span className="text-[9px] text-[#5f6b7e]">
                        Keeps the image and its approval
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Words against the budget the script was written to. The A/V editor shows its own
          per-chapter version; this is the narrative format's equivalent, so a class running
          long is visible in either. */}
      {artifact.kind === 'script' && !isAvScript && artifact.measured && (
        <div className="flex items-center gap-3 flex-wrap text-[11px] mb-2">
          <span className="text-[#c7d0e0]">
            {artifact.measured.total_words?.toLocaleString()} words
            {artifact.budget?.total_words
              ? ` of ${artifact.budget.total_words.toLocaleString()}`
              : ''}
          </span>
          <span className={
            artifact.budget?.total_words
              && artifact.measured.total_words > artifact.budget.total_words * 1.1
              ? 'text-[#f7a23b]' : 'text-[#8a95a8]'
          }>
            {artifact.measured.runtime_minutes} min
            {artifact.budget?.minutes ? ` of ${artifact.budget.minutes}` : ''}
            {' '}at {artifact.measured.wpm} wpm
          </span>
        </div>
      )}

      {/* Content: rendered, or the right editor for this artifact's format.
          Quiz and exam are structured JSON, so they get a question editor rather
          than the prose editor. Before this they had no working editor at all:
          the pencil set editing=true and the JSON branch just re-rendered the
          viewer. */}
      {isAvScript ? (
        <AvScriptEditor
          artifactId={artifact.id}
          canApprove={canApproveContent}
          onPlaceImage={({ cid, sid }) => setComposer({ placement: 'slide', cid, sid })}
        />
      ) : editing && isQuiz ? (
        <QuizEditor
          value={artifact.generated_content}
          saving={saveMut.isPending}
          // QuizEditor hands back normalized rows; the artifact stores { questions }.
          onSave={(questions) => saveMut.mutate(JSON.stringify({ questions }))}
          onCancel={() => setEditing(false)}
        />
      ) : editing ? (
        <>
          <ArtifactEditor
            value={artifact.format === 'html'
              ? hydrateCaptions(artifact.generated_content, byId)
              : artifact.generated_content}
            format={artifact.format}
            onSave={(html) => saveMut.mutate(html)}
            onCancel={() => setEditing(false)}
            onEditorReady={setEditor}
            onInsertImage={() => setComposer({})}
            renderBubbleMenu={(ed) => (
              <ImageBubbleMenu
                editor={ed}
                canApprove={canApproveContent}
                onRefine={({ imageId }) => setComposer({ imageId })}
                onEditCaption={({ imageId }) => {
                  const target = images.find((i) => String(i.id) === String(imageId));
                  setCaptionEdit({
                    id: Number(imageId),
                    caption: target?.caption || '',
                    alt_text: target?.alt_text || '',
                  });
                }}
                onChanged={refresh}
              />
            )}
          />
          {composer && !composer.placement && (
            <ImageComposer
              artifactId={artifact.id}
              editor={editor}
              target={composer.imageId ? composer : null}
              onClose={() => setComposer(null)}
              onInserted={refresh}
            />
          )}
        </>
      ) : (
        <div className="border border-[#1d2634] rounded-lg p-4 bg-[#0b0e13] max-h-[52vh] overflow-auto">
          {isQuiz ? (
            <QuizView content={artifact.generated_content} />
          ) : artifact.format === 'html' ? (
            // Sanitized server side on save with a strict allowlist, so this is
            // already safe to render. See TrainingContent::HtmlSanitizer.
            <div className={MD}
              dangerouslySetInnerHTML={{
                __html: toViewerHtml(artifact.generated_content, byId, coverCount),
              }} />
          ) : artifact.generated_content ? (
            <div className={MD}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{artifact.generated_content}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-xs text-[#8a95a8]">
              Empty. Use “Steer with JPRO” below, or edit directly.
            </div>
          )}
        </div>
      )}

      {/* Cover composer sits outside the editor branch: a cover can be added
          without opening the editor at all. */}
      {(composer?.placement === 'cover' || composer?.placement === 'standalone') && (
        <ImageComposer
          artifactId={artifact.id}
          editor={null}
          target={null}
          placement={composer.placement}
          onClose={() => setComposer(null)}
          onInserted={refresh}
        />
      )}

      {/* An A/V script slide. There is no TipTap document to insert into, so the saved
          image is anchored to the slide, which is what puts it in the VIDEO cell. */}
      {composer?.placement === 'slide' && (
        <ImageComposer
          artifactId={artifact.id}
          editor={null}
          target={null}
          placement="slide"
          onClose={() => setComposer(null)}
          onInserted={async (img) => {
            if (!img?.id) return;
            try {
              // No content_rev: placement is idempotent and the image was just created, so
              // there is nothing of anyone else's to overwrite. Stated rather than left as
              // an omission, since every other mutating call carries one.
              await anchorScriptImage(artifact.id, img.id, { cid: composer.cid, sid: composer.sid });
              qc.invalidateQueries({ queryKey: ['av-script', artifact.id] });
            } catch (e) {
              toast.error(e?.message || 'Saved the image but could not place it on the slide');
            }
          }}
        />
      )}

      {/* Steer with AI */}
      <div className="mt-4 border-t border-[#1d2634] pt-3">
        <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0] mb-2">Steer with JPRO</div>
        <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)}
          placeholder="e.g. add a worked AMI calculation example; make section 2 more concise; add a summary table"
          className="w-full text-xs bg-transparent border border-[#2b3a4d] rounded-lg px-3 py-2 min-h-[64px]" />
        <button onClick={() => reviseMut.mutate()} disabled={reviseMut.isPending || !instruction.trim()}
          className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-[#1f6feb] text-white disabled:opacity-40">
          {reviseMut.isPending ? 'Revising…' : 'Revise this artifact'}
        </button>
        <div className="text-[10px] text-[#5f6b7e] mt-1">
          Revising rewrites the artifact and returns it to draft for re-approval.
        </div>
      </div>
    </>
  );
}
